import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAreas, getObligations, getResponses, saveResponses, getStats, getAuditLogs, getDocuments, uploadDocument, deleteDocument, sendReport } from '../services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  ArrowLeft, CheckCircle2, XCircle, MinusCircle, 
  FileText, Clock, Upload, Download, Trash2, History,
  BarChart3, Building2, AlertTriangle, Mail, Loader2
} from 'lucide-react';

// Logo ICL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a3de8ce8-9ebc-47a8-b532-221c9e0f1005/artifacts/0dlj7jep_LOGO%20ICL.png";

const DashboardPage = ({ company, onBack }) => {
  const { user } = useAuth();
  const [areas, setAreas] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [responses, setResponses] = useState({});
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedObligation, setSelectedObligation] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('questionnaire');
  const [sendingReport, setSendingReport] = useState(false);
  const [reportSent, setReportSent] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [areasData, obligationsData, responsesData, statsData, logsData, docsData] = await Promise.all([
        getAreas(),
        getObligations(),
        getResponses(company.id),
        getStats(company.id),
        getAuditLogs(company.id),
        getDocuments(company.id)
      ]);
      
      setAreas(areasData);
      setObligations(obligationsData);
      
      // Convert responses array to map
      const responsesMap = {};
      responsesData.forEach(r => {
        responsesMap[r.obligation_id] = r;
      });
      setResponses(responsesMap);
      
      setStats(statsData);
      setAuditLogs(logsData);
      setDocuments(docsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  }, [company.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleResponse = async (obligationId, value) => {
    const currentResponse = responses[obligationId] || {};
    const newResponse = {
      obligation_id: obligationId,
      value: value,
      notes: currentResponse.notes || '',
      document_ids: currentResponse.document_ids || []
    };

    // Optimistic update
    setResponses(prev => ({
      ...prev,
      [obligationId]: { ...newResponse, updated_at: new Date().toISOString() }
    }));

    setSaving(true);
    try {
      await saveResponses(company.id, [newResponse]);
      // Refresh stats
      const newStats = await getStats(company.id);
      setStats(newStats);
      const newLogs = await getAuditLogs(company.id, 10);
      setAuditLogs(newLogs);
    } catch (error) {
      console.error('Error saving response:', error);
    }
    setSaving(false);
  };

  const handleNotes = async (obligationId, notes) => {
    const currentResponse = responses[obligationId] || {};
    if (!currentResponse.value) return;
    
    const newResponse = {
      obligation_id: obligationId,
      value: currentResponse.value,
      notes: notes,
      document_ids: currentResponse.document_ids || []
    };

    setResponses(prev => ({
      ...prev,
      [obligationId]: { ...newResponse, updated_at: new Date().toISOString() }
    }));

    try {
      await saveResponses(company.id, [newResponse]);
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedObligation) return;
    
    try {
      await uploadDocument(company.id, file, selectedObligation.id);
      const newDocs = await getDocuments(company.id);
      setDocuments(newDocs);
    } catch (error) {
      console.error('Error uploading document:', error);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await deleteDocument(docId);
      const newDocs = await getDocuments(company.id);
      setDocuments(newDocs);
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    setReportSent(false);
    try {
      await sendReport(company.id);
      setReportSent(true);
      setTimeout(() => setReportSent(false), 5000);
    } catch (error) {
      console.error('Error sending report:', error);
      alert('Error al enviar el informe. Inténtalo de nuevo.');
    }
    setSendingReport(false);
  };

  const getStatusColor = (percentage) => {
    if (percentage >= 85) return 'emerald';
    if (percentage >= 70) return 'amber';
    return 'rose';
  };

  const getStatusText = (percentage) => {
    if (percentage >= 85) return 'ALTO';
    if (percentage >= 70) return 'MEDIO';
    return 'BAJO';
  };

  const getObligationsForArea = (areaId) => {
    return obligations.filter(o => o.area_id === areaId);
  };

  const getAreaStats = (areaId) => {
    if (!stats) return null;
    return stats.by_area.find(a => a.area_id === areaId);
  };

  const CircularProgress = ({ percentage, size = 180 }) => {
    const color = getStatusColor(percentage);
    const strokeWidth = 12;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    
    const colors = {
      emerald: '#10B981',
      amber: '#F59E0B',
      rose: '#E11D48'
    };

    return (
      <div className="circular-progress" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            className="progress-bg"
            cx={size / 2}
            cy={size / 2}
            r={radius}
          />
          <circle
            className="progress-bar"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colors[color]}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="progress-text">
          <div className="text-4xl font-bold text-slate-900">{percentage}%</div>
          <div className={`text-sm font-semibold text-${color}-600`}>
            {getStatusText(percentage)}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onBack}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ICL Logo" className="h-10 w-auto" />
              <div>
                <span className="text-lg font-bold tracking-tight block">{company.name}</span>
                <span className="text-xs text-slate-500 mono">{company.cif || 'Sin CIF'}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {saving && <span className="text-sm text-slate-500">Guardando...</span>}
            {reportSent && (
              <span className="text-sm text-emerald-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Informe enviado
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendReport}
              disabled={sendingReport}
              data-testid="send-report-btn"
              className="border-sky-200 text-sky-700 hover:bg-sky-50"
            >
              {sendingReport ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              {sendingReport ? 'Enviando...' : 'Enviar informe'}
            </Button>
            <Badge variant="outline" className="text-slate-600">
              <Building2 className="w-3 h-3 mr-1" />
              {company.employees_count || '–'} empleados
            </Badge>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="results" data-testid="tab-results">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Resultados
            </TabsTrigger>
            <TabsTrigger value="questionnaire" data-testid="tab-questionnaire">
              <FileText className="w-4 h-4 mr-2" />
              Cuestionario
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <History className="w-4 h-4 mr-2" />
              Auditoría
            </TabsTrigger>
          </TabsList>

          {/* DASHBOARD TAB */}
          <TabsContent value="dashboard">
            <div className="bento-grid">
              {/* Hero metric */}
              <Card className="col-span-full lg:col-span-3 row-span-2 border-slate-200">
                <CardHeader>
                  <p className="overline">CUMPLIMIENTO GLOBAL</p>
                  <CardTitle className="text-xl">Nivel de cumplimiento</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center pb-8">
                  <CircularProgress percentage={stats?.percentage || 0} />
                  <div className="mt-6 flex gap-6 text-center">
                    <div>
                      <div className="text-2xl font-bold text-emerald-600">{stats?.compliant || 0}</div>
                      <div className="text-xs text-slate-500">Cumple</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-rose-600">{stats?.non_compliant || 0}</div>
                      <div className="text-xs text-slate-500">No cumple</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-400">{stats?.not_applicable || 0}</div>
                      <div className="text-xs text-slate-500">N/A</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Area chart */}
              <Card className="col-span-full lg:col-span-9 row-span-2 border-slate-200">
                <CardHeader>
                  <p className="overline">CUMPLIMIENTO POR ÁREA</p>
                  <CardTitle className="text-xl">12 áreas normativas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats?.by_area.map((area) => {
                      const color = getStatusColor(area.percentage);
                      return (
                        <div key={area.area_id} className="flex items-center gap-4">
                          <div className="w-8 text-right text-sm font-medium text-slate-500">
                            {area.area_code}.
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-700 truncate pr-4">
                                {area.area_name}
                              </span>
                              <span className={`text-sm font-bold text-${color}-600`}>
                                {area.percentage}%
                              </span>
                            </div>
                            <Progress 
                              value={area.percentage} 
                              className="h-2"
                              style={{
                                '--progress-background': color === 'emerald' ? '#10B981' : color === 'amber' ? '#F59E0B' : '#E11D48'
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Quick stats */}
              <Card className="col-span-full md:col-span-2 lg:col-span-4 border-slate-200">
                <CardHeader className="pb-2">
                  <p className="overline">OBLIGACIONES</p>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{stats?.total_obligations || 0}</div>
                  <p className="text-sm text-slate-500">Total de requisitos</p>
                </CardContent>
              </Card>

              <Card className="col-span-full md:col-span-2 lg:col-span-4 border-slate-200">
                <CardHeader className="pb-2">
                  <p className="overline">COMPLETADAS</p>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{stats?.completed || 0}</div>
                  <p className="text-sm text-slate-500">Respuestas registradas</p>
                </CardContent>
              </Card>

              <Card className="col-span-full md:col-span-2 lg:col-span-4 border-slate-200">
                <CardHeader className="pb-2">
                  <p className="overline">DOCUMENTOS</p>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900">{documents.length}</div>
                  <p className="text-sm text-slate-500">Evidencias subidas</p>
                </CardContent>
              </Card>

              {/* Recent activity */}
              <Card className="col-span-full lg:col-span-6 border-slate-200">
                <CardHeader>
                  <p className="overline">ACTIVIDAD RECIENTE</p>
                  <CardTitle className="text-lg">Últimos cambios</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[200px]">
                    {auditLogs.length === 0 ? (
                      <p className="text-sm text-slate-500">No hay actividad registrada</p>
                    ) : (
                      <div className="space-y-3">
                        {auditLogs.slice(0, 10).map((log) => (
                          <div key={log.id} className="flex items-start gap-3 text-sm">
                            <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                            <div>
                              <p className="text-slate-700">{log.details}</p>
                              <p className="text-xs text-slate-400">
                                {log.user_name} · {new Date(log.created_at).toLocaleString('es-ES')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Pending items */}
              <Card className="col-span-full lg:col-span-6 border-slate-200">
                <CardHeader>
                  <p className="overline">PENDIENTES</p>
                  <CardTitle className="text-lg">Áreas con bajo cumplimiento</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[200px]">
                    <div className="space-y-3">
                      {stats?.by_area
                        .filter(a => a.percentage < 70)
                        .sort((a, b) => a.percentage - b.percentage)
                        .map((area) => (
                          <div key={area.area_id} className="flex items-center gap-3 p-2 bg-rose-50 rounded-lg">
                            <AlertTriangle className="w-4 h-4 text-rose-600" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-700">{area.area_name}</p>
                              <p className="text-xs text-slate-500">{area.non_compliant} obligaciones sin cumplir</p>
                            </div>
                            <Badge variant="outline" className="text-rose-600 border-rose-200">
                              {area.percentage}%
                            </Badge>
                          </div>
                        ))}
                      {stats?.by_area.filter(a => a.percentage < 70).length === 0 && (
                        <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg">
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                          <p className="text-sm text-emerald-700">Todas las áreas tienen cumplimiento aceptable</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* RESULTS TAB - Detailed responses */}
          <TabsContent value="results">
            <Card className="border-slate-200">
              <CardHeader>
                <p className="overline">RESUMEN EJECUTIVO</p>
                <CardTitle>Resultados detallados del cuestionario</CardTitle>
                <CardDescription>
                  Vista completa de todas las respuestas organizadas por área de cumplimiento.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Global summary */}
                <div className="grid grid-cols-4 gap-4 mb-8 p-4 bg-slate-50 rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-slate-900">{stats?.percentage || 0}%</div>
                    <div className="text-xs text-slate-500 mt-1">Cumplimiento Global</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-emerald-600">{stats?.compliant || 0}</div>
                    <div className="text-xs text-slate-500 mt-1">Cumple (SÍ)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-rose-600">{stats?.non_compliant || 0}</div>
                    <div className="text-xs text-slate-500 mt-1">No cumple (NO)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-slate-400">{stats?.not_applicable || 0}</div>
                    <div className="text-xs text-slate-500 mt-1">No aplica (N/A)</div>
                  </div>
                </div>

                {/* Detailed results by area */}
                <Accordion type="multiple" className="space-y-4" defaultValue={areas.map(a => `results-${a.id}`)}>
                  {areas.map((area) => {
                    const areaStats = getAreaStats(area.id);
                    const areaObligations = getObligationsForArea(area.id);
                    const color = areaStats ? getStatusColor(areaStats.percentage) : 'slate';
                    
                    // Count responses for this area
                    const areaResponses = areaObligations.map(o => responses[o.id]).filter(Boolean);
                    const siCount = areaResponses.filter(r => r.value === 'SI').length;
                    const noCount = areaResponses.filter(r => r.value === 'NO').length;
                    const naCount = areaResponses.filter(r => r.value === 'NA').length;
                    const pendingCount = areaObligations.length - areaResponses.length;
                    
                    return (
                      <AccordionItem 
                        key={area.id} 
                        value={`results-${area.id}`}
                        className="border border-slate-200 rounded-lg overflow-hidden"
                        data-testid={`results-area-${area.code}`}
                      >
                        <AccordionTrigger className="hover:no-underline px-6 py-4 bg-slate-50">
                          <div className="flex items-center gap-4 flex-1 text-left">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white ${
                              color === 'emerald' ? 'bg-emerald-500' : color === 'amber' ? 'bg-amber-500' : 'bg-rose-500'
                            }`}>
                              {area.code}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-900">{area.name}</h3>
                              <div className="flex items-center gap-4 mt-1 text-xs">
                                <span className="text-emerald-600 font-medium">{siCount} SÍ</span>
                                <span className="text-rose-600 font-medium">{noCount} NO</span>
                                <span className="text-slate-400 font-medium">{naCount} N/A</span>
                                {pendingCount > 0 && (
                                  <span className="text-amber-600 font-medium">{pendingCount} pendientes</span>
                                )}
                              </div>
                            </div>
                            <Badge 
                              className={`status-${color === 'emerald' ? 'high' : color === 'amber' ? 'medium' : 'low'} text-base px-4 py-1`}
                            >
                              {areaStats?.percentage || 0}%
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4">
                          <div className="space-y-2 mt-2">
                            {areaObligations.map((obligation) => {
                              const response = responses[obligation.id];
                              return (
                                <div 
                                  key={obligation.id}
                                  className={`flex items-start gap-3 p-3 rounded-lg border ${
                                    response?.value === 'SI' ? 'bg-emerald-50 border-emerald-200' :
                                    response?.value === 'NO' ? 'bg-rose-50 border-rose-200' :
                                    response?.value === 'NA' ? 'bg-slate-50 border-slate-200' :
                                    'bg-amber-50 border-amber-200'
                                  }`}
                                >
                                  <div className="flex-shrink-0 mt-0.5">
                                    {response?.value === 'SI' && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                                    {response?.value === 'NO' && <XCircle className="w-5 h-5 text-rose-600" />}
                                    {response?.value === 'NA' && <MinusCircle className="w-5 h-5 text-slate-400" />}
                                    {!response?.value && <AlertTriangle className="w-5 h-5 text-amber-500" />}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="mono text-sm font-semibold text-slate-500">{obligation.code}</span>
                                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                                        response?.value === 'SI' ? 'bg-emerald-200 text-emerald-800' :
                                        response?.value === 'NO' ? 'bg-rose-200 text-rose-800' :
                                        response?.value === 'NA' ? 'bg-slate-200 text-slate-600' :
                                        'bg-amber-200 text-amber-800'
                                      }`}>
                                        {response?.value || 'PENDIENTE'}
                                      </span>
                                    </div>
                                    <p className="text-sm text-slate-700 mt-1">{obligation.description}</p>
                                    {response?.notes && (
                                      <p className="text-xs text-slate-500 mt-2 italic bg-white p-2 rounded border border-slate-100">
                                        Notas: {response.notes}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* QUESTIONNAIRE TAB */}
          <TabsContent value="questionnaire">
            <Card className="border-slate-200">
              <CardHeader>
                <p className="overline">CUESTIONARIO SGI LABOUR COMPLIANCE</p>
                <CardTitle>Evaluación de cumplimiento normativo</CardTitle>
                <CardDescription>
                  Selecciona SÍ, NO o N/A para cada obligación. Los cambios se guardan automáticamente.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="space-y-4">
                  {areas.map((area) => {
                    const areaStats = getAreaStats(area.id);
                    const areaObligations = getObligationsForArea(area.id);
                    const color = areaStats ? getStatusColor(areaStats.percentage) : 'slate';
                    
                    return (
                      <AccordionItem 
                        key={area.id} 
                        value={area.id}
                        className="border border-slate-200 rounded-lg px-6"
                        data-testid={`area-${area.code}`}
                      >
                        <AccordionTrigger className="hover:no-underline py-4">
                          <div className="flex items-center gap-4 flex-1 text-left">
                            <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-sm font-bold text-slate-600">
                              {area.code}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-900">{area.name}</h3>
                              <p className="text-sm text-slate-500">{areaObligations.length} obligaciones</p>
                            </div>
                            {areaStats && (
                              <Badge 
                                className={`status-${color === 'emerald' ? 'high' : color === 'amber' ? 'medium' : 'low'}`}
                              >
                                {areaStats.percentage}%
                              </Badge>
                            )}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-6">
                          <div className="space-y-4 mt-2">
                            {areaObligations.map((obligation) => {
                              const response = responses[obligation.id];
                              return (
                                <div 
                                  key={obligation.id}
                                  className="p-4 bg-slate-50 rounded-lg border border-slate-100"
                                  data-testid={`obligation-${obligation.code}`}
                                >
                                  <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="mono text-sm font-medium text-slate-500">
                                          {obligation.code}
                                        </span>
                                        {response?.value === 'SI' && (
                                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                        )}
                                        {response?.value === 'NO' && (
                                          <XCircle className="w-4 h-4 text-rose-500" />
                                        )}
                                        {response?.value === 'NA' && (
                                          <MinusCircle className="w-4 h-4 text-slate-400" />
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-700">{obligation.description}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleResponse(obligation.id, 'SI')}
                                        className={`response-btn ${response?.value === 'SI' ? 'active-si' : ''}`}
                                        data-testid={`btn-si-${obligation.code}`}
                                      >
                                        SÍ
                                      </button>
                                      <button
                                        onClick={() => handleResponse(obligation.id, 'NO')}
                                        className={`response-btn ${response?.value === 'NO' ? 'active-no' : ''}`}
                                        data-testid={`btn-no-${obligation.code}`}
                                      >
                                        NO
                                      </button>
                                      <button
                                        onClick={() => handleResponse(obligation.id, 'NA')}
                                        className={`response-btn ${response?.value === 'NA' ? 'active-na' : ''}`}
                                        data-testid={`btn-na-${obligation.code}`}
                                      >
                                        N/A
                                      </button>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => {
                                          setSelectedObligation(obligation);
                                          setSheetOpen(true);
                                        }}
                                        data-testid={`btn-details-${obligation.code}`}
                                      >
                                        <FileText className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUDIT TAB */}
          <TabsContent value="audit">
            <Card className="border-slate-200">
              <CardHeader>
                <p className="overline">REGISTRO DE AUDITORÍA</p>
                <CardTitle>Historial de cambios</CardTitle>
                <CardDescription>
                  Registro completo de todas las acciones realizadas en esta empresa
                </CardDescription>
              </CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No hay registros de auditoría</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {auditLogs.map((log) => (
                      <div 
                        key={log.id}
                        className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100"
                      >
                        <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                          <History className="w-4 h-4 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{log.action}</p>
                          <p className="text-sm text-slate-600">{log.details}</p>
                          <p className="text-xs text-slate-400 mt-1">
                            {log.user_name} · {new Date(log.created_at).toLocaleString('es-ES')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Details Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[400px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <span className="mono text-slate-500">{selectedObligation?.code}</span>
              Detalles
            </SheetTitle>
            <SheetDescription>
              {selectedObligation?.description}
            </SheetDescription>
          </SheetHeader>
          
          {selectedObligation && (
            <div className="mt-6 space-y-6">
              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Notas</label>
                <Textarea
                  placeholder="Añade notas o comentarios..."
                  value={responses[selectedObligation.id]?.notes || ''}
                  onChange={(e) => handleNotes(selectedObligation.id, e.target.value)}
                  className="min-h-[100px]"
                  data-testid="obligation-notes"
                />
              </div>

              {/* Documents */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Documentos de evidencia</label>
                <div className="space-y-2">
                  {documents
                    .filter(d => d.obligation_id === selectedObligation.id)
                    .map((doc) => (
                      <div 
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-4 h-4 text-slate-500" />
                          <div>
                            <p className="text-sm font-medium text-slate-700">{doc.filename}</p>
                            <p className="text-xs text-slate-500">
                              {(doc.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" data-testid={`download-${doc.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteDocument(doc.id)}
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-rose-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                    <Upload className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-600">Subir documento</span>
                    <input 
                      type="file" 
                      className="hidden" 
                      onChange={handleFileUpload}
                      data-testid="upload-document"
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DashboardPage;
